import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Quote.scss';
import Button from '../../atoms/Button/Button';
import Modal from '../Modal/Modal';
import UnitImages from '../UnitImages/UnitImages';
import UnitInformation from '../UnitInformation/UnitInformation';
import Card from '../../atoms/Card/Card';
import { UNIT_STATUSES } from '../../../appConstants';
import { formatDateFromSFDC } from '../../../helpers/utils/generic';
import { getUnitPrimaryImage } from '../../../api/salesforce';

const Quote = ({
  unit,
  unit: {
    unitStatus,
    unitName,
    estate,
    monthlyRent,
    unitId,
    premium,
    bedrooms,
    postCode,
    moveInEnd,
    moveInStart,
    wishList,
    furnished,
    paymentPeriodicity,
    agreementType,
    tenancyLength,
    breakClause,
  },
  selected,
  index,
  onReserveHome,
}) => {
  const [modalState, setModalState] = useState({
    seeMore: false,
    seeWishList: false,
  });
  const moveInFirstDate = moveInStart && formatDateFromSFDC(moveInStart);
  const moveInEndDate = moveInEnd && formatDateFromSFDC(moveInEnd);
  const unitUnavailable = !UNIT_STATUSES.includes(unitStatus);
  const premiumBanner = premium === 'Premium';
  const shouldShowContractFields = /live work|nha|non-housing|occupation contract/i.test(agreementType || '');
  const unitNameWithPostCode = postCode && !(unitName || '').includes(postCode)
    ? `${unitName}, ${postCode}`
    : unitName;
  const [imageKeys, setImageKeys] = useState({
    images: [],
    floorplanUrl: undefined,
    epcUrl: undefined,
  });
  const [isPrimaryImageLoading, setIsPrimaryImageLoading] = useState(!!unitId);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!unitId) {
        if (!cancelled) {
          setImageKeys({ images: [], floorplanUrl: undefined, epcUrl: undefined });
          setIsPrimaryImageLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setIsPrimaryImageLoading(true);
      }

      try {
        const primaryUrl = await getUnitPrimaryImage(unitId);
        if (!cancelled) {
          setImageKeys({
            images: primaryUrl ? [primaryUrl] : [],
            floorplanUrl: undefined,
            epcUrl: undefined,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setImageKeys({ images: [], floorplanUrl: undefined, epcUrl: undefined });
        }
      } finally {
        if (!cancelled) {
          setIsPrimaryImageLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [unitId]);
  return (
    <div
      className={`quote ${selected ? 'quote--selected' : ''}`}
      disabled={unitUnavailable}
      data-testid="quote"
    >
      <Card>
        <UnitImages images={imageKeys.images} isLoading={isPrimaryImageLoading} />
        { unitUnavailable
          && <div className="quote__banner">This home is no longer available</div>}

        <div className="quote__content">
          {premiumBanner && (
            <p className="unit-attribute premium" data-testid="quote-premium">
              {premium}
            </p>
          )}
          <h2
            data-testid="quote-unit-name"
            dangerouslySetInnerHTML={{ __html: unitNameWithPostCode }}
          />
          <h3
            data-testid="quote-estate-name"
            dangerouslySetInnerHTML={{ __html: estate }}
          />
          <p className="unit-attribute">
            {`£${monthlyRent}/month`}
          </p>
          <p className="unit-attribute">
            { furnished }
          </p>
          <p className="unit-attribute">
            { bedrooms }
          </p>
          <Button
            title="See more"
            onClick={() => setModalState({ ...modalState, seeMore: true })}
            disabled={unitUnavailable}
            plain
          />
          {modalState.seeMore && (
            <Modal
              modalState={modalState}
              modalName="seeMore"
              setModalState={setModalState}
            >

              <h3
                data-testid="quote-estate-name"
                dangerouslySetInnerHTML={{ __html: estate }}
              />
              { imageKeys.floorplanUrl
                && (
                <>
                  <a
                    href={imageKeys.floorplanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button button--plain"
                  >
                    See Floorplan
                  </a>
                  <br />
                  <br />
                </>

                )}
              { imageKeys.epcUrl
                && (
                <a
                  href={imageKeys.epcUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button button--plain"
                >
                  See EPC certificate
                </a>
                )}

              <UnitInformation {...unit} />
            </Modal>
          )}
          <div className="quote__terms">
            <h4>Payment Periodicity:</h4>
            <p className="unit-attribute">{paymentPeriodicity || '-'}</p>
            <h4>Fixed Move in Date:</h4>
            {moveInFirstDate && moveInEndDate && (
              <p className="quote__term">
                {`${moveInFirstDate} `}
              </p>
            )}
            {shouldShowContractFields && (
              <>
                <h4>Tenancy Length:</h4>
                <p className="unit-attribute">{tenancyLength || '-'}</p>
                <h4>Break Clause:</h4>
                <p className="unit-attribute">{breakClause || '-'}</p>
              </>
            )}
          </div>
          <div className="quote__button">
            <Button
              title="Reserve this home"
              disabled={unitUnavailable}
              onClick={() => onReserveHome(index)}
              testId="choose-this-home"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

Quote.propTypes = {
  unit: PropTypes.shape({
    unitName: PropTypes.string.isRequired,
    quotes: PropTypes.instanceOf(Array).isRequired,
    unitStatus: PropTypes.string.isRequired,
    estate: PropTypes.string.isRequired,
    monthlyRent: PropTypes.number.isRequired,
    unitId: PropTypes.string,
    premium: PropTypes.string,
    floorLevel: PropTypes.string,
    moveInStart: PropTypes.string,
    moveInEnd: PropTypes.string,
    imageUrl: PropTypes.string,
    wishList: PropTypes.string,
    furnished: PropTypes.string,
    bedrooms: PropTypes.string,
    postCode: PropTypes.string,
    paymentPeriodicity: PropTypes.string,
    agreementType: PropTypes.string,
    tenancyLength: PropTypes.string,
    breakClause: PropTypes.string,
  }),
  bedrooms: PropTypes.string,
  bathrooms: PropTypes.string,
  buildingAmenities: PropTypes.string,
  petFriendly: PropTypes.bool,
  unitAmenities: PropTypes.instanceOf(Array),
  selected: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  onReserveHome: PropTypes.func.isRequired,
  flatNo: PropTypes.string,
  streetNo: PropTypes.string,
  streetName: PropTypes.string,
  city: PropTypes.string,
  postCode: PropTypes.string,
  outdoorSpace: PropTypes.string,
  aspect: PropTypes.string,
  internalSpaceFt: PropTypes.number,
  keyFeatures: PropTypes.string,
  accessibility: PropTypes.string,
};

Quote.defaultProps = {
  unit: {
    wishList: null,
    premium: null,
    moveInStart: null,
    moveInEnd: null,
    unitId: null,
    floorLevel: null,
    imageUrl: null,
    furnished: null,
    bedrooms: null,
    postCode: null,
    paymentPeriodicity: null,
    agreementType: null,
    tenancyLength: null,
    breakClause: null,
  },
  petFriendly: null,
  city: null,
  internalSpaceFt: null,
  flatNo: null,
  streetNo: null,
  streetName: null,
  accessibility: '',
  aspect: null,
  bedrooms: null,
  bathrooms: null,
  keyFeatures: null,
  postCode: null,
  outdoorSpace: null,
  buildingAmenities: null,
  unitAmenities: null,
};

export default Quote;
